import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./FAQPage.css";
import logo from "../assets/logo.jpg";
import SEOHead from "./SEOHead";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  const categories = [
    {
      label: "General",
      icon: "💡",
      faqs: [
        { q: "What is ComLinkr?", a: "ComLinkr is a community-first platform that connects people to housing, food, and local communities matched to their culture and lifestyle — so you feel at home, not just housed." },
        { q: "Who is ComLinkr for?", a: "Everyone — international movers, graduate students, remote workers, families, and even locals who want to explore their own city better. If you want to feel more connected to where you live, ComLinkr is for you." },
        { q: "Is ComLinkr only for people moving abroad?", a: "No. ComLinkr serves both movers and locals. Whether you're relocating across the world or just discovering hidden gems in your own city, the platform adapts to your needs." },
      ],
    },
    {
      label: "Features",
      icon: "🏠",
      faqs: [
        { q: "What are ComLinkr's four main features?", a: "Housing (culturally-matched properties and roommates), Food (restaurant and cuisine discovery), Community (local Q&A and connections), and Feed (local news, events, city guides, and social posts)." },
        { q: "How does the Housing feature work?", a: "ComLinkr analyses your culture, lifestyle, dietary habits, and work style to suggest compatible housing options and roommates — not just by price and location, but by values and personality." },
        { q: "What's in the Feed?", a: "The Feed includes local, national, and global news across Business, Technology, Health, Sports, and Entertainment — plus community events like food festivals, tech meetups, markets, yoga classes, and more." },
        { q: "Can I discover restaurants through ComLinkr?", a: "Yes! The Food feature helps you discover restaurants, cafés, and markets matched to your cuisine preferences — from Italian to Indian, Thai to Chinese — wherever you are." },
      ],
    },
    {
      label: "Matching & Culture",
      icon: "🌍",
      faqs: [
        { q: "How does cultural matching work?", a: "We factor food and lifestyle values, social habits, language preferences, and background to suggest compatible communities, roommates, and living setups that feel right for you." },
        { q: "What makes ComLinkr different from Rightmove or Zoopla?", a: "We go beyond listings. ComLinkr matches you based on cultural compatibility, lifestyle values, food preferences, and community connections — so you don't just find a flat, you find belonging." },
        { q: "Does ComLinkr support multiple countries and cities?", a: "Yes. ComLinkr is designed for global use, with plans to cover 50+ cities across multiple countries, with news and events localised to your chosen location." },
      ],
    },
    {
      label: "Trust & Safety",
      icon: "🔒",
      faqs: [
        { q: "How do you ensure safety and authenticity?", a: "Verified profiles, community ratings, and authenticity checks help keep the platform trustworthy and safe for everyone." },
        { q: "Is my data secure?", a: "Yes. We take data privacy seriously and follow best practices to ensure your information is stored securely and never shared without your consent." },
      ],
    },
    {
      label: "Early Access",
      icon: "🚀",
      faqs: [
        { q: "How do I get early access?", a: "Sign up via the Early Access form on our homepage. Early members get exclusive beta features before public launch and help shape the platform through feedback." },
        { q: "When will ComLinkr launch publicly?", a: "We're in active development. Early access sign-ups are the best way to stay updated on our launch timeline." },
      ],
    },
  ];

  let globalIndex = 0;

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <div className="faq-page">
      <SEOHead
        title="FAQ — ComLinkr | Frequently Asked Questions"
        description="Got questions about ComLinkr? Find answers about how our housing, food, community, and feed features work — and how to get early access."
        canonical="https://www.comlinkr.com/faq"
      />
      <button className="back-btn-faq" onClick={() => navigate("/")}>← Back to Home</button>

      {/* Hero */}
      <section className="faq-hero">
        <div className="container text-center">
          <motion.img
            src={logo}
            alt="ComLinkr"
            className="faq-logo"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, type: "spring" }}
          />
          <motion.h1
            className="faq-title"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Frequently Asked <span className="faq-highlight">Questions</span>
          </motion.h1>
          <motion.p
            className="faq-sub"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Everything you need to know about ComLinkr — how it works, what it offers, and why it's different.
          </motion.p>
        </div>
      </section>

      {/* FAQs */}
      <section className="faq-body">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {categories.map((cat, ci) => (
                <motion.div
                  key={ci}
                  className="faq-category"
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: ci * 0.08 }}
                >
                  <div className="faq-cat-header">
                    <span className="faq-cat-icon">{cat.icon}</span>
                    <h3 className="faq-cat-title">{cat.label}</h3>
                  </div>

                  {cat.faqs.map((faq, fi) => {
                    const idx = globalIndex++;
                    return (
                      <div key={fi} className="faq-item-page">
                        <button
                          className={`faq-q-btn ${openIndex === idx ? "open" : ""}`}
                          onClick={() => toggle(idx)}
                        >
                          <span>{faq.q}</span>
                          <span className="faq-chevron">{openIndex === idx ? "−" : "+"}</span>
                        </button>
                        <AnimatePresence initial={false}>
                          {openIndex === idx && (
                            <motion.div
                              className="faq-a-wrap"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.28 }}
                            >
                              <p className="faq-a-text">{faq.a}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="faq-cta-light">
        <div className="container text-center">
          <span className="faq-eyebrow">Still curious?</span>
          <h2 className="faq-cta-title">Still have questions?</h2>
          <p className="faq-cta-sub">We'd love to hear from you. Reach out and we'll get back to you shortly.</p>
          <div className="faq-cta-btns">
            <a href="mailto:admin@comlinkr.com" className="btn-faq-primary">
              Contact Us
            </a>
            <button className="btn-faq-outline" onClick={() => navigate("/")}>Back to Home</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
